/**
 * Campaign SMS text row in creative pickers (length hint for GSM segment limit).
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import { Check, EyeOff } from 'lucide-react';

import { LeadsListTags } from '@/features/smswiser-campaigns/components/shared';
import { SMS_TEXT_MAX_LENGTH, smsEffectiveGsmLength } from '@/features/campaign-texts/smsTextLimit';
import { cn } from '@/lib/utils';

/** Full text display, no truncation. Optionally shows performance tags. */
export const TextPickerItem: FC<{
  text: string;
  isSelected: boolean;
  onToggle: () => void;
  performanceTags?: string[];
  tagColors?: Record<string, string>;
  pendingHide?: boolean;
  /** Old selection (replaced by AI Save/Use & Hide) — distinct styling. */
  replacedByAi?: boolean;
  /** When set, row is highlighted if text exceeds this length (SMS segments). */
  lengthLimit?: number;
}> = ({
  text,
  isSelected,
  onToggle,
  performanceTags,
  tagColors,
  pendingHide,
  replacedByAi,
  lengthLimit = SMS_TEXT_MAX_LENGTH,
}) => {
  const hasTags = (performanceTags?.length ?? 0) > 0;
  const overLimit =
    lengthLimit != null && smsEffectiveGsmLength(text) > lengthLimit;
  return (
    <div
      role="button"
      tabIndex={0}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onToggle}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onToggle()}
      className={cn(
        'flex cursor-pointer items-start gap-3 rounded-lg border px-3 py-2 transition-colors',
        overLimit && 'border-destructive/70 bg-destructive/10 ring-1 ring-destructive/25',
        !overLimit && isSelected && 'border-primary/40 bg-primary/5',
        !overLimit &&
          !isSelected &&
          replacedByAi &&
          'border-muted-foreground/30 bg-muted/30 hover:border-muted-foreground/40',
        !overLimit && !isSelected && !replacedByAi && 'border-transparent bg-background hover:border-border',
      )}
    >
      <span
        className={cn(
          'mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border',
          overLimit && isSelected && 'border-destructive bg-destructive',
          overLimit && !isSelected && 'border-destructive/50',
          !overLimit && isSelected && 'border-primary bg-primary',
          !overLimit && !isSelected && 'border-input bg-background',
        )}
      >
        {isSelected && (
          <Check
            className={cn(
              'h-2.5 w-2.5 stroke-[2.5]',
              overLimit ? 'text-destructive-foreground' : 'text-primary-foreground',
            )}
          />
        )}
      </span>
      <div className="min-w-0 flex-1">
        <span
          className={cn(
            'break-words whitespace-pre-wrap text-left text-xs',
            overLimit ? 'text-destructive' : 'text-muted-foreground',
          )}
        >
          {text || <em>Empty</em>}
        </span>
        {overLimit && (
          <span className="ml-1.5 inline-flex rounded bg-destructive/15 px-1 py-0.5 text-[10px] font-semibold text-destructive">
            {smsEffectiveGsmLength(text)}/{lengthLimit}
          </span>
        )}
        {replacedByAi && !pendingHide && (
          <span className="ml-1 inline-flex items-center gap-0.5 text-[10px] font-medium text-muted-foreground">
            Previous selection
          </span>
        )}
        {pendingHide && (
          <span className="ml-1 inline-flex items-center gap-0.5 text-[10px] font-medium text-amber-600">
            <EyeOff className="h-2.5 w-2.5" /> Hides after save
          </span>
        )}
        {hasTags && (
          <LeadsListTags
            performanceTags={performanceTags ?? []}
            tagColors={tagColors}
            className="ml-1.5 mt-1 inline-flex"
            compact
            disableTooltip
          />
        )}
      </div>
    </div>
  );
};
